import React, { useEffect, useMemo, useRef, useState } from "react";
import { Animated, Dimensions, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useToast } from "../../components/LhToastContainer";
import { AppDispatch, RootState } from "../../store";
import { setSelectedTicketId, setTicketDetailSource, saveSustainQueueListState, SustainQueueRow, TicketStatus } from "../../store/slices/ticketingPortalSlice";
import { fetchSustainQueue } from "../../store/thunks/ticketingPortalThunk";
import { mergeIntoExistingTicket, fetchPanelDuplicates, TicketingDuplicateCandidate } from "../../store/thunks/submitTicketThunk";
import styles from "./SustainQueueScreenStyles";
import mobileStyles, { STATUS_LEFT_BORDER_COLOR, STATUS_DOT_COLOR } from "./SustainQueueScreen.mobile.style";
import useSustainQueueListing, { EMPTY_TABLE_FILTERS } from "./hooks/useSustainQueueListing";
import SustainQueueHeader from "./components/SustainQueueHeader";
import SustainQueueStatusSummary from "./components/SustainQueueStatusSummary";
import SustainQueueKanban from "./components/SustainQueueKanban";
import SustainQueueTable from "./components/SustainQueueTable";
import SustainQueueFilterBar from "./components/SustainQueueFilterBar";
import SustainQueueFilterSheet, {
  SustainMobileFilters,
  EMPTY_SUSTAIN_FILTERS,
} from "./components/SustainQueueFilterSheet";
import DuplicatesPanel from "./components/DuplicatesPanel";
import { CloseTicketModal } from "./components/CloseTicketModal";
import useTicketingAccess from "./hooks/useTicketingAccess";
import LHLoader from "../../components/LHLoader";
import { updateSustainTicket } from "../../store/thunks/ticketingPortalThunk";
import { fetchSubmitTicketFormConfigAndFields } from "../../store/thunks/submitTicketThunk";
import { isCategoryConfigStale } from "../../utils/ticketingCategoryBeta";
import { closeTicketWithRca } from "./util/closeTicketWithRca";
import { useTheme } from "../../theme";

type Props = {
  onNavigate: (screen: string) => void;
};

/* ── helpers ────────────────────────────────────────────────── */
const URGENCY_LABEL: Record<string, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

const URGENCY_DARK: Record<string, { bg: string; text: string; dot: string }> = {
  critical: { bg: "#3A1414", text: "#F28B82", dot: "#F28B82" },
  high:     { bg: "#2E1C08", text: "#FFB066", dot: "#FFB066" },
  medium:   { bg: "#2E2800", text: "#FFD966", dot: "#FFD966" },
  low:      { bg: "#2B2B2B", text: "#B0B0B0", dot: "#6E6E6E" },
};

const STATUS_LABEL: Record<string, string> = {
  new: "New",
  progress: "In Progress",
  "waiting-it": "Escalated to IT",
  "waiting-tmv": "Escalated to TMV",
  "submitted-ado": "Submitted to ADO",
  "waiting-user": "Waiting on User",
  resolved: "Closed",
};

const STATUS_TEXT_STYLE: Record<string, object> = {
  new: mobileStyles.statusNewText,
  progress: mobileStyles.statusProgressText,
  "waiting-it": mobileStyles.statusWaitingItText,
  "waiting-tmv": mobileStyles.statusWaitingTmvText,
  "submitted-ado": mobileStyles.statusSubmittedAdoText,
  "waiting-user": mobileStyles.statusWaitingUserText,
  resolved: mobileStyles.statusResolvedText,
};

const STATUS_BG_STYLE: Record<string, object> = {
  new: mobileStyles.statusNew,
  progress: mobileStyles.statusProgress,
  "waiting-it": mobileStyles.statusWaitingIt,
  "waiting-tmv": mobileStyles.statusWaitingTmv,
  "submitted-ado": mobileStyles.statusSubmittedAdo,
  "waiting-user": mobileStyles.statusWaitingUser,
  resolved: mobileStyles.statusResolved,
};

// Dark-mode badge palettes (match SustainQueueTable / SustainQueueKanban dark badges)
const URGENCY_BADGE_DARK: Record<string, { bg: string; text: string }> = {
  Critical: { bg: "#4A1A14", text: "#FF8A78" },
  High:     { bg: "#3D2608", text: "#FFAD5C" },
  Medium:   { bg: "#3A2E12", text: "#FFD966" },
  Low:      { bg: "#2B2B2B", text: "#9CA3AF" },
};

const STATUS_BADGE_DARK: Record<string, { bg: string; text: string }> = {
  new:             { bg: "#1B2A3D", text: "#6AADFF" },
  progress:        { bg: "#26204A", text: "#A89EFF" },
  "waiting-it":    { bg: "#3A2E12", text: "#FFD966" },
  "waiting-tmv":   { bg: "#3A2E12", text: "#FFD966" },
  "submitted-ado": { bg: "#3D2608", text: "#FFAD5C" },
  "waiting-user":  { bg: "#3A2E12", text: "#FFD966" },
  resolved:        { bg: "#2B2B2B", text: "#9CA3AF" },
};

const KANBAN_SECTION_ORDER = [
  "new",
  "progress",
  "waiting-it",
  "waiting-tmv",
  "submitted-ado",
  "waiting-user",
  "resolved",
] as const;

const SkeletonBlock: React.FC<{
  pulse: Animated.Value;
  height: number;
  width?: number | string;
  radius?: number;
  style?: any;
}> = ({ pulse, height, width = "100%", radius = 6, style }) => (
  <Animated.View
    style={[
      {
        height,
        width,
        borderRadius: radius,
        backgroundColor: "#E5E7EB",
        opacity: pulse,
      },
      style,
    ]}
  />
);

const QueuePaginationControls: React.FC<{
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isDark?: boolean;
}> = ({ totalCount, currentPage, totalPages, pageSize, onPageChange, isDark }) => {
  const pageNumbers = useMemo(() => {
    if (totalPages <= 0) return [] as number[];
    const maxButtons = 5;
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - maxButtons + 1));
    const end = Math.min(totalPages, start + maxButtons - 1);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [currentPage, totalPages]);

  return (
    <View style={paginationStyles.wrap}>
      <Text style={[paginationStyles.metaText, isDark && { color: "#B0B0B0" }]}>
        {totalCount === 0
          ? "No tickets"
          : `Showing ${Math.min((currentPage - 1) * pageSize + 1, totalCount)}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount}`}
      </Text>

      <View style={paginationStyles.pageControlsRow}>
      <Pressable
        style={[paginationStyles.button, isDark && { backgroundColor: "#202020", borderColor: "#404040" }, currentPage <= 1 && paginationStyles.buttonDisabled, currentPage <= 1 && isDark && { backgroundColor: "#1a1a1a", borderColor: "#363636" }]}
        onPress={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
      >
        <MaterialIcons name="chevron-left" size={16} color={currentPage <= 1 ? "#6E6E6E" : (isDark ? "#3D8BFF" : "#005CBC")} />
        <Text style={[paginationStyles.buttonText, isDark && { color: "#3D8BFF" }, currentPage <= 1 && paginationStyles.buttonTextDisabled, currentPage <= 1 && isDark && { color: "#6E6E6E" }]}>Prev</Text>
      </Pressable>

      {pageNumbers[0] > 1 && (
        <>
          <Pressable style={[paginationStyles.pageNumberBtn, isDark && { backgroundColor: "#202020", borderColor: "#404040" }]} onPress={() => onPageChange(1)}>
            <Text style={[paginationStyles.pageNumberText, isDark && { color: "#3D8BFF" }]}>1</Text>
          </Pressable>
          {pageNumbers[0] > 2 && <Text style={[paginationStyles.ellipsis, isDark && { color: "#6E6E6E" }]}>...</Text>}
        </>
      )}

      {pageNumbers.map((pageNumber) => (
        <Pressable
          key={pageNumber}
          style={[paginationStyles.pageNumberBtn, isDark && { backgroundColor: "#202020", borderColor: "#404040" }, currentPage === pageNumber && paginationStyles.pageNumberBtnActive]}
          onPress={() => onPageChange(pageNumber)}
        >
          <Text style={[paginationStyles.pageNumberText, isDark && currentPage !== pageNumber && { color: "#3D8BFF" }, currentPage === pageNumber && paginationStyles.pageNumberTextActive]}>{pageNumber}</Text>
        </Pressable>
      ))}

      {pageNumbers[pageNumbers.length - 1] < totalPages && (
        <>
          {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && <Text style={[paginationStyles.ellipsis, isDark && { color: "#6E6E6E" }]}>...</Text>}
          <Pressable style={[paginationStyles.pageNumberBtn, isDark && { backgroundColor: "#202020", borderColor: "#404040" }]} onPress={() => onPageChange(totalPages)}>
            <Text style={[paginationStyles.pageNumberText, isDark && { color: "#3D8BFF" }]}>{totalPages}</Text>
          </Pressable>
        </>
      )}

      <Pressable
        style={[paginationStyles.button, isDark && { backgroundColor: "#202020", borderColor: "#404040" }, currentPage >= totalPages && paginationStyles.buttonDisabled, currentPage >= totalPages && isDark && { backgroundColor: "#1a1a1a", borderColor: "#363636" }]}
        onPress={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
      >
        <Text style={[paginationStyles.buttonText, isDark && { color: "#3D8BFF" }, currentPage >= totalPages && paginationStyles.buttonTextDisabled, currentPage >= totalPages && isDark && { color: "#6E6E6E" }]}>Next</Text>
        <MaterialIcons name="chevron-right" size={16} color={currentPage >= totalPages ? "#6E6E6E" : (isDark ? "#3D8BFF" : "#005CBC")} />
      </Pressable>
      </View>
    </View>
  );
};

/* ── Card sub-component ─────────────────────────────────────── */
const MobileCard: React.FC<{
  ticket: SustainQueueRow;
  showStatus?: boolean;
  onPress: () => void;
}> = ({ ticket, showStatus, onPress }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const urgencyLabel = URGENCY_LABEL[ticket.urgency] ?? "Low";
  const urgDark = isDark ? (URGENCY_DARK[ticket.urgency] ?? URGENCY_DARK.low) : null;
  const statusLabel = STATUS_LABEL[ticket.status] ?? ticket.status;
  const isUnassigned = ticket.assignedTo === "Unassigned";
  const borderColor = STATUS_LEFT_BORDER_COLOR[ticket.status] ?? "#d0d0d0";
  const initials = isUnassigned
    ? ""
    : ticket.assigneeInitials ??
      ticket.assignedTo
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

  const urgencyDark = isDark ? URGENCY_BADGE_DARK[urgencyLabel] : undefined;
  const statusDark = isDark ? STATUS_BADGE_DARK[ticket.status] : undefined;
  const urgencyBadge = (
    <View
      style={[
        mobileStyles.badge,
        mobileStyles[`urgency${urgencyLabel}` as keyof typeof mobileStyles] as object,
        urgencyDark && { backgroundColor: urgencyDark.bg },
      ]}
    >
      <View
        style={[
          mobileStyles.badgeDot,
          mobileStyles[
            `urgency${urgencyLabel}Dot` as keyof typeof mobileStyles
          ] as object,
          urgencyDark && { backgroundColor: urgencyDark.text },
        ]}
      />
      <Text
        style={[
          mobileStyles.badgeText,
          mobileStyles[
            `urgency${urgencyLabel}Text` as keyof typeof mobileStyles
          ] as object,
          urgencyDark && { color: urgencyDark.text },
        ]}
      >
        {urgencyLabel}
      </Text>
    </View>
  );

  return (
    <Pressable
      style={({ pressed }) => [
        mobileStyles.card,
        isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default },
        pressed ? mobileStyles.cardPressed : null,
      ]}
      onPress={onPress}
    >
      {/* Left accent border */}
      <View style={[mobileStyles.cardLeftBorder, { backgroundColor: borderColor }]} />

      <View style={mobileStyles.cardContent}>
        {/* Top row: ticket ID + urgency/status badge */}
        <View style={mobileStyles.cardTopRow}>
          <Text style={[mobileStyles.ticketId, isDark && { color: colors.brand.primary }]}>{ticket.id}</Text>
          {showStatus ? (
            /* List view: show status badge */
            <View style={[mobileStyles.badge, STATUS_BG_STYLE[ticket.status], statusDark ? { backgroundColor: statusDark.bg } : null]}>
              <Text style={[mobileStyles.badgeText, STATUS_TEXT_STYLE[ticket.status], statusDark ? { color: statusDark.text } : null]}>
                {statusLabel}
              </Text>
            </View>
          ) : (
            /* Kanban view: show urgency badge */
            urgencyBadge
          )}
        </View>

        {/* Title */}
        <Text style={[mobileStyles.ticketTitle, isDark && { color: colors.text.primary }]} numberOfLines={2}>
          {ticket.title}
        </Text>

        {/* Bottom row */}
        <View style={mobileStyles.cardBottomRow}>
          {/* Left: urgency (list view) or assignee (kanban) */}
          {showStatus ? (
            /* List view: urgency on left */
            urgencyBadge
          ) : (
            /* Kanban view: assignee on left */
            isUnassigned ? (
              <View style={[mobileStyles.unassignedChip, isDark && { backgroundColor: URGENCY_BADGE_DARK.Critical.bg }]}>
                <Text style={[mobileStyles.unassignedChipText, isDark && { color: URGENCY_BADGE_DARK.Critical.text }]}>Unassigned</Text>
              </View>
            ) : (
              <View style={mobileStyles.assigneeRow}>
                <View style={mobileStyles.assigneeAvatar}>
                  <Text style={mobileStyles.assigneeAvatarText}>{initials}</Text>
                </View>
                <Text style={[mobileStyles.assigneeName, isDark && { color: colors.text.primary }]}>{ticket.assignedTo.split(" ")[0]} {ticket.assignedTo.split(" ")[1]?.[0]}.</Text>
              </View>
            )
          )}

          {/* Right: meta info */}
          {showStatus ? (
            /* List view: assignee · info on right */
            <Text style={[mobileStyles.locationMeta, isDark && { color: colors.text.secondary }]} numberOfLines={1}>
              {isUnassigned ? "Unassigned" : ticket.assignedTo.split(" ")[0] + " " + (ticket.assignedTo.split(" ")[1]?.[0] ?? "") + "."}{" "}
              · {(ticket.impactSites ?? 0) === 1 ? "1 site" : `${ticket.impactSites ?? 0} sites`}
            </Text>
          ) : (
            /* Kanban view: location · aging on right */
            <Text style={[mobileStyles.locationMeta, isDark && { color: colors.text.secondary }]} numberOfLines={1}>
              {ticket.location} · {ticket.aging}
            </Text>
          )}
        </View>
      </View>
    </Pressable>
  );
};

/* ── Main screen ────────────────────────────────────────────── */
const SustainQueueScreen: React.FC<Props> = ({ onNavigate }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { canViewSustainFeatures, hasWriteAccess, isReadOnly, accessLoading } = useTicketingAccess();

  // Route guard – redirect users who cannot view sustain features
  useEffect(() => {
    if (!accessLoading && !canViewSustainFeatures) {
      onNavigate("Ticketing Portal");
    }
  }, [accessLoading, canViewSustainFeatures, onNavigate]);

  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const {
    sustainQueueLoading,
    query,
    activeStatus,
    viewMode,
    kanbanFilters,
    subtitle,
    totalCount,
    filteredTickets,
    filteredTicketsForTable,
    groupedForKanban,
    assignableUsers,
    filterOptions,
    statusPills,
    statusCounts,
    setQuery,
    setActiveStatus,
    setViewMode,
    setKanbanFilters,
    tableFilters,
    setTableFilters,
    moveTicketToStatus,
    quickUpdateTicket,
    currentPage,
    totalPages,
    pageSize,
    goToPage,
    teams,
    viewingTeamId,
    setViewingTeamId,
    isViewingOwnTeam,
    managerOptions,
    matchesManagerFilter,
    refreshTeams,
  } = useSustainQueueListing();

  // Viewing another team's queue is always read-only, on top of the existing
  // Super-User read-only rule.
  const effectiveReadOnly = isReadOnly || !isViewingOwnTeam;

  const windowWidth = Dimensions.get("window").width;
  const isiPhone = Platform.OS === "ios" && windowWidth < 768;

  // Mobile supports kanban-only. Force the shared view state accordingly.
  useEffect(() => {
    if (isiPhone && viewMode !== "kanban") {
      setViewMode("kanban");
    }
  }, [isiPhone, viewMode, setViewMode]);

  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [mobileFilters, setMobileFilters] = useState<SustainMobileFilters>(EMPTY_SUSTAIN_FILTERS);
  const [duplicatePanelOpen, setDuplicatePanelOpen] = useState(false);
  const [selectedDuplicateTicketId, setSelectedDuplicateTicketId] = useState<string | null>(null);
  const [mergingTicketId, setMergingTicketId] = useState<string | null>(null);
  const [panelDuplicates, setPanelDuplicates] = useState<SustainQueueRow[] | undefined>(undefined);
  const [panelDuplicatesLoading, setPanelDuplicatesLoading] = useState(false);
  const [showCloseTicketModal, setShowCloseTicketModal] = useState(false);
  const [pendingCloseUpdate, setPendingCloseUpdate] = useState<{
    ticketId: string;
    updates: Partial<Pick<SustainQueueRow, "title" | "urgency" | "status" | "assignedTo">>;
  } | null>(null);

  // Get all tickets from Redux to find duplicates
  const sustainQueue = useSelector((state: RootState) => state.ticketingPortal.sustainQueue);
  const queueError = useSelector((state: RootState) => state.ticketingPortal.error);
  const categoryConfig = useSelector((state: RootState) => state.ticketingPortal.submitTicketCategoryConfig);
  const authUserEmail = useSelector(
    (state: RootState) => (state as any).auth?.user?.email || (state as any).auth?.user?.username || ""
  );

  const categoryConfigStale = useSelector(isCategoryConfigStale);

  useEffect(() => {
    if (categoryConfigStale) {
      dispatch(fetchSubmitTicketFormConfigAndFields() as any);
    }
  }, [categoryConfigStale, dispatch]);

  // Read saved list state snapshot — used to extract lastOpenedTicketId for scroll restoration
  const sustainQueueListState = useSelector(
    (state: RootState) => state.ticketingPortal.sustainQueueListState
  );

  /**
   * Ticket ID to scroll to after list renders (restored from Redux on Back navigation).
   * Populated once from saved list state then cleared to avoid re-scrolling.
   */
  const [scrollToTicketId, setScrollToTicketId] = useState<string | null>(
    () => sustainQueueListState?.lastOpenedTicketId ?? null
  );

  // Clear scrollToTicketId after data has loaded and rendered once.
  useEffect(() => {
    if (!sustainQueueLoading && scrollToTicketId && filteredTickets.length > 0) {
      const timer = setTimeout(() => setScrollToTicketId(null), 800);
      return () => clearTimeout(timer);
    }
  }, [sustainQueueLoading, filteredTickets.length, scrollToTicketId]);

  /** Save current list state to Redux so it can be restored when the user presses Back. */
  const saveCurrentListState = (ticketId: string) => {
    dispatch(saveSustainQueueListState({
      query,
      activeStatus,
      viewMode,
      kanbanFilters,
      currentPage,
      lastOpenedTicketId: ticketId,
    }));
  };

  const handleTicketPress = (ticketId: string) => {
    saveCurrentListState(ticketId);
    dispatch(setSelectedTicketId(ticketId));
    dispatch(setTicketDetailSource("Sustain Queue"));
    onNavigate("Ticket Detail");
  };

  // Maps raw API status strings (from the duplicate-check endpoint) to internal
  // TicketStatus keys so the panel always shows the server's current value.
  const DUPLICATE_API_STATUS_MAP: Record<string, TicketStatus> = {
    "New":              "new",
    "In Progress":      "progress",
    "Escalated to IT":    "waiting-it",
    "Escalated to TMV":   "waiting-tmv",
    "Submitted to ADO": "submitted-ado",
    "Waiting on User":  "waiting-user",
    "Waiting on user":  "waiting-user",
    "Closed":           "resolved",
  };

  const handleDuplicateBadgePress = async (ticketId: string) => {
    setSelectedDuplicateTicketId(ticketId);
    setPanelDuplicates(undefined);
    setPanelDuplicatesLoading(true);
    setDuplicatePanelOpen(true);

    const ticket = sustainQueue.find((t) => t.id === ticketId);
    if (ticket?.categoryNumId && ticket?.subCategoryNumId) {
      try {
        const candidates: TicketingDuplicateCandidate[] = await dispatch(
          fetchPanelDuplicates(ticket.categoryNumId, ticket.subCategoryNumId) as any
        );
        const matched = candidates
          .map((c) => {
            const t = sustainQueue.find((t) => t.backendId === c.id || t.id === c.ticketNumber);
            if (!t) return null;
            // Always use the fresh status from the API response rather than the
            // (potentially stale) value cached in local Redux state.
            const freshStatus: TicketStatus = DUPLICATE_API_STATUS_MAP[c.status] ?? t.status;
            return freshStatus !== t.status ? { ...t, status: freshStatus } : t;
          })
          .filter((t): t is SustainQueueRow => !!t && t.id !== ticketId);
        setPanelDuplicates(matched);
      } catch {
        setPanelDuplicates([]);
      }
    }
    setPanelDuplicatesLoading(false);
  };

  const handleViewDuplicateDetails = (ticketId: string) => {
    saveCurrentListState(ticketId);
    setDuplicatePanelOpen(false);
    setTimeout(() => {
      dispatch(setSelectedTicketId(ticketId));
      dispatch(setTicketDetailSource("Sustain Queue"));
      onNavigate("Ticket Detail");
    }, 200);
  };

  const handleMergeFromPanel = async (duplicateTicketId: string) => {
    const primaryTicket = sustainQueue.find((t) => t.id === selectedDuplicateTicketId);
    const targetTicket = sustainQueue.find((t) => t.id === duplicateTicketId);

    if (!primaryTicket?.backendId || !targetTicket?.backendId) {
      toast.show("Unable to merge: ticket IDs not available", "error");
      return;
    }

    setMergingTicketId(duplicateTicketId);
    try {
      // targetTicketId = the duplicate we're merging INTO (stays open)
      // sourceTicketId = the primary flagged ticket (gets absorbed)
      await dispatch(
        mergeIntoExistingTicket(targetTicket.backendId, primaryTicket.backendId, authUserEmail) as any
      );
      toast.show(`${primaryTicket.id} merged into ${duplicateTicketId}`, "success");
      setDuplicatePanelOpen(false);
      setSelectedDuplicateTicketId(null);
      setPanelDuplicates(undefined);
      dispatch(fetchSustainQueue() as any);
    } catch {
      toast.show("Merge failed. Please try again.", "error");
    } finally {
      setMergingTicketId(null);
    }
  };

  /**
   * Guard: a ticket must be assigned before its status can change.
   * Used by drag-and-drop, quick-edit modal (Kanban) and inline status
   * dropdown (Table). When the operation also sets a non-Unassigned
   * assignee in the same update (quick-edit modal), the block is lifted.
   */
  const handleMoveTicket = (ticketId: string, nextStatus: TicketStatus) => {
    const ticket = sustainQueue.find((t) => t.id === ticketId);
    if (ticket?.assignedTo === "Unassigned") {
      toast.show(
        "Assign the ticket to a team member before changing its status.",
        "error"
      );
      return;
    }

    if (nextStatus === "resolved" && ticket && ticket.status !== "resolved") {
      setPendingCloseUpdate({
        ticketId,
        updates: {
          status: "resolved",
        },
      });
      setShowCloseTicketModal(true);
      return;
    }

    moveTicketToStatus(ticketId, nextStatus);
  };

  const handleQuickUpdate = (
    ticketId: string,
    updates: Partial<Pick<SustainQueueRow, "title" | "urgency" | "status" | "assignedTo">>
  ) => {
    // Only enforce when a status change is attempted
    if (updates.status !== undefined) {
      const ticket = sustainQueue.find((t) => t.id === ticketId);
      // Resolve the effective assignee after this update
      const effectiveAssignee = updates.assignedTo ?? ticket?.assignedTo;
      if (!effectiveAssignee || effectiveAssignee === "Unassigned") {
        toast.show(
          "Assign the ticket to a team member before changing its status.",
          "error"
        );
        return;
      }

      if (updates.status === "resolved" && ticket && ticket.status !== "resolved") {
        setPendingCloseUpdate({ ticketId, updates });
        setShowCloseTicketModal(true);
        return;
      }
    }

    quickUpdateTicket(ticketId, updates);
  };

  const closeModalTicket = pendingCloseUpdate
    ? sustainQueue.find((ticket) => ticket.id === pendingCloseUpdate.ticketId)
    : undefined;

  const handleCloseFromKanbanQuickEdit = async (
    catId: number,
    subCatId: number,
    rcaCategory: string,
    rcaNotes: string,
    resolution: string,
    resolvedBy: string
  ): Promise<{ msg: string; type: "success" | "error" | "info" | "warning" }> => {
    if (!pendingCloseUpdate || !closeModalTicket) {
      return {
        msg: "Close action context expired. Please try again.",
        type: "error",
      };
    }

    const result = await closeTicketWithRca({
      dispatch,
      ticketId: pendingCloseUpdate.ticketId,
      meta: {
        categoryId: catId,
        subCategoryId: subCatId,
        rcaCategory,
        rcaNotes,
        resolution,
        resolvedBy,
      },
      performCloseUpdate: async (meta) => {
        await dispatch(
          updateSustainTicket(
            pendingCloseUpdate.ticketId,
            {
              status: "resolved",
              urgency: pendingCloseUpdate.updates.urgency,
              assignedTo: pendingCloseUpdate.updates.assignedTo,
            },
            {
              search: query.trim() || undefined,
              statuses: kanbanFilters.statuses,
              urgencies: kanbanFilters.urgencies,
              categories: kanbanFilters.categories,
              subcategories: kanbanFilters.subcategories,
              locations: [
                ...kanbanFilters.locations,
                ...kanbanFilters.markets,
                ...kanbanFilters.regions,
              ],
              assignees: kanbanFilters.assignees,
              pageNumber: currentPage,
              pageSize,
            },
            {
              categoryId: meta.categoryId,
              subCategoryId: meta.subCategoryId,
              rcaCategory: meta.rcaCategory,
              resolvedBy: meta.resolvedBy,
            }
          ) as any
        );
        await refreshTeams();
      },
    });

    if (result.type === "success") {
      toast.show(result.msg, "success");
    }

    return result;
  };

  /* Apply mobile filters on top of hook's filteredTickets */
  const mobileFilteredTickets = useMemo(() => {
    const STATUS_KEY_MAP: Record<string, string> = {
      "New": "new", "In Progress": "progress", "Escalated to IT": "waiting-it",
      "Escalated to TMV": "waiting-tmv", "Submitted to ADO": "submitted-ado",
      "Waiting on User": "waiting-user", "Closed": "resolved",
    };
    let result = filteredTickets;
    if (mobileFilters.status) {
      const key = STATUS_KEY_MAP[mobileFilters.status];
      if (key) result = result.filter((t) => t.status === key);
    }
    if (mobileFilters.urgency) {
      result = result.filter((t) => t.urgency.toLowerCase() === mobileFilters.urgency.toLowerCase());
    }
    if (mobileFilters.category) {
      result = result.filter((t) => t.category === mobileFilters.category);
    }
    if (mobileFilters.assignee) {
      result = result.filter((t) => t.assignedTo === mobileFilters.assignee);
    }
    if (mobileFilters.location) {
      const locName = mobileFilters.location.split(" (SAP")[0].split(" (Market")[0].split(" (Region")[0];
      result = result.filter((t) => t.location.toLowerCase().includes(locName.toLowerCase()));
    }
    if (mobileFilters.tertiary) {
      result = result.filter((t) => t.tertiary === mobileFilters.tertiary);
    }
    if (mobileFilters.manager) {
      result = result.filter((t) => matchesManagerFilter(t, [mobileFilters.manager]));
    }
    return result;
  }, [filteredTickets, mobileFilters, matchesManagerFilter]);

  /* Grouped by status for mobile kanban view */
  const mobileKanbanGroups = useMemo(() => {
    const byStatus: Record<string, SustainQueueRow[]> = {};
    KANBAN_SECTION_ORDER.forEach((k) => { byStatus[k] = []; });
    mobileFilteredTickets.forEach((t) => {
      if (byStatus[t.status]) byStatus[t.status].push(t);
    });
    return KANBAN_SECTION_ORDER.map((key) => ({ statusKey: key, tickets: byStatus[key] })).filter(
      (g) => g.tickets.length > 0
    );
  }, [mobileFilteredTickets]);

  const activeFilterCount = [
    mobileFilters.status,
    mobileFilters.urgency,
    mobileFilters.category,
    mobileFilters.tertiary,
    mobileFilters.assignee,
    mobileFilters.manager,
    mobileFilters.location,
    mobileFilters.dateRange,
  ].filter(Boolean).length;

  const snackAnim = useRef(new Animated.Value(-80)).current;
  const [filterSnackbar, setFilterSnackbar] = useState<{ msg: string; isSuccess: boolean } | null>(null);
  const skeletonPulse = useRef(new Animated.Value(0.55)).current;

  useEffect(() => {
    if (!sustainQueueLoading) {
      skeletonPulse.setValue(0.55);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(skeletonPulse, { toValue: 1, duration: 650, useNativeDriver: true }),
        Animated.timing(skeletonPulse, { toValue: 0.55, duration: 650, useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [sustainQueueLoading, skeletonPulse]);

  const showFilterSnackbar = (msg: string, isSuccess: boolean = false) => {
    setFilterSnackbar({ msg, isSuccess });
    snackAnim.setValue(-80);
    Animated.sequence([
      Animated.timing(snackAnim, { toValue: 0, duration: 300, useNativeDriver: true }),
      Animated.delay(2500),
      Animated.timing(snackAnim, { toValue: -80, duration: 250, useNativeDriver: true }),
    ]).start(() => setFilterSnackbar(null));
  };

  // Prevent unauthorized content from flashing while access resolves
  if (accessLoading || !canViewSustainFeatures) {
    return <LHLoader />;
  }

  /* ── iPhone layout ────────────────────────────────────────── */
  if (isiPhone) {
    return (
      <View style={[mobileStyles.screenRoot, isDark && { backgroundColor: colors.background.page }]}>
        <SustainQueueHeader
          mobile
          subtitle={subtitle}
          totalCount={totalCount}
          filterCount={activeFilterCount}
          viewMode="kanban"
          onBackToPortal={() => onNavigate("Ticketing Portal")}
          onChangeView={() => setViewMode("kanban")}
          onFiltersPress={() => setFilterSheetOpen(true)}
        />

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingBottom: 32 }}
          showsVerticalScrollIndicator={false}
        >
          {/* Search bar */}
          <View style={mobileStyles.searchWrap}>
            <MaterialIcons
              name="search"
              size={18}
              color={isDark ? colors.text.secondary : '#818181'}
              style={mobileStyles.searchIcon}
            />
            <TextInput
              style={[
                mobileStyles.searchInput,
                isDark && {
                  backgroundColor: colors.background.elevated,
                  borderColor: colors.border.default,
                  color: colors.text.primary,
                },
              ]}
              value={query}
              onChangeText={setQuery}
              placeholder="Search Ticket ID, Title, Submitter"
              placeholderTextColor={isDark ? colors.text.secondary : '#9099aa'}
            />
          </View>

          {/* Error state */}
          {queueError && (
            <View style={[
              mobileStyles.cardList,
              {
                backgroundColor: isDark ? colors.background.elevated : '#fff3cd',
                borderWidth: 1,
                borderColor: isDark ? colors.border.default : '#ffc107',
                borderRadius: 8,
                padding: 16,
                marginHorizontal: 12,
                marginTop: 12,
              },
            ]}
            >
              <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 12 }}>
                <MaterialIcons name="error" size={24} color="#FF6B6B" style={{ marginTop: 2 }} />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: "600", color: isDark ? '#FFFFFF' : '#333', marginBottom: 4 }}>
                    Unable to load tickets
                  </Text>
                  <Text style={{ fontSize: 13, color: isDark ? '#D1D5DB' : '#555', lineHeight: 18 }}>
                    Please refresh the page or try again later.
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Card list */}
          <View style={mobileStyles.cardList}>
            {!queueError && sustainQueueLoading ? (
              <>
                {[0, 1, 2].map((col) => (
                  <View key={`m-k-${col}`} style={{ marginBottom: 10 }}>
                    <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 8 }}>
                      <SkeletonBlock pulse={skeletonPulse} height={10} width={120} radius={5} />
                    </View>
                    {[0, 1].map((row) => (
                      <View key={`m-k-${col}-${row}`} style={[mobileStyles.card, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }, { marginBottom: 8 }] as any}>
                        <SkeletonBlock pulse={skeletonPulse} height={10} width={120} style={{ marginBottom: 10 }} />
                        <SkeletonBlock pulse={skeletonPulse} height={12} width="86%" style={{ marginBottom: 6 }} />
                        <SkeletonBlock pulse={skeletonPulse} height={12} width="62%" style={{ marginBottom: 10 }} />
                        <SkeletonBlock pulse={skeletonPulse} height={10} width="55%" />
                      </View>
                    ))}
                  </View>
                ))}
              </>
            ) : (
              /* Kanban: vertical sections by status */
              mobileKanbanGroups.map((group) => (
                <View key={group.statusKey}>
                  <View style={mobileStyles.sectionHeader}>
                    <View style={mobileStyles.sectionHeaderLeft}>
                      <View
                        style={[
                          mobileStyles.sectionDot,
                          { backgroundColor: STATUS_DOT_COLOR[group.statusKey] ?? "#7a8495" },
                        ]}
                      />
                      <Text style={[mobileStyles.sectionLabel, isDark && { color: colors.text.primary }]}>
                        {STATUS_LABEL[group.statusKey]}
                      </Text>
                    </View>
                    <View style={[mobileStyles.sectionCountWrap, isDark && { backgroundColor: colors.background.elevated }]}>
                      <Text style={[mobileStyles.sectionCount, isDark && { color: colors.text.primary }]}>{group.tickets.length}</Text>
                    </View>
                  </View>
                  {group.tickets.map((ticket) => (
                    <MobileCard
                      key={ticket.id}
                      ticket={ticket}
                      showStatus={false}
                      onPress={() => handleTicketPress(ticket.id)}
                    />
                  ))}
                </View>
              ))
            )}
            {!queueError && !sustainQueueLoading && mobileFilteredTickets.length === 0 ? (
              <View style={[{ alignItems: "center", paddingVertical: 48 }, isDark && { backgroundColor: colors.background.page }]}>
                {totalCount === 0 && !query.trim() && activeFilterCount === 0 ? (
                  <>
                    <MaterialIcons name="inbox" size={40} color="#c0c8d4" style={{ marginBottom: 12 }} />
                    <Text style={{ fontSize: 16, fontWeight: "600", color: isDark ? '#FFFFFF' : '#3a3a3a', marginBottom: 6 }}>
                      No tickets have been submitted yet.
                    </Text>
                    <Text style={{ fontSize: 14, color: isDark ? '#D1D5DB' : '#818181', textAlign: "center" }}>
                      Tickets will appear here once they are submitted to the Sustain Queue.
                    </Text>
                  </>
                ) : (
                  <>
                    <MaterialIcons name="search-off" size={40} color="#c0c8d4" style={{ marginBottom: 12 }} />
                    <Text style={{ fontSize: 16, fontWeight: "600", color: isDark ? '#FFFFFF' : '#3a3a3a', marginBottom: 6 }}>
                      No tickets match the selected filters
                    </Text>
                    <Text style={{ fontSize: 14, color: isDark ? '#D1D5DB' : '#818181', textAlign: "center" }}>
                      Try adjusting your search or filter criteria.
                    </Text>
                  </>
                )}
              </View>
            ) : null}

            <QueuePaginationControls
              totalCount={totalCount}
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={goToPage}
            />
          </View>
        </ScrollView>

        <SustainQueueFilterSheet
          visible={filterSheetOpen}
          filters={mobileFilters}
          categoryOptions={filterOptions.categories}
          tertiaryOptions={filterOptions.tertiaries}
          assigneeOptions={assignableUsers}
          managerOptions={managerOptions}
          locationOptions={[
            ...filterOptions.locations,
            ...filterOptions.markets,
            ...filterOptions.regions,
          ]}
          onApply={(f) => setMobileFilters(f)}
          onClose={() => setFilterSheetOpen(false)}
          onSnackbar={showFilterSnackbar}
        />
        {filterSnackbar && (
          <Animated.View
            style={{
              position: "absolute",
              top: 14,
              left: 12,
              right: 12,
              zIndex: 999,
              borderRadius: 10,
              backgroundColor: filterSnackbar.isSuccess
                ? (isDark ? "#1a4a1a" : "#2d6a2d")
                : (isDark ? colors.background.elevated : "#1a2332"),
              borderWidth: isDark ? 1 : 0,
              borderColor: isDark ? colors.border.default : "transparent",
              flexDirection: "row",
              alignItems: "center",
              paddingHorizontal: 16,
              paddingVertical: 14,
              gap: 10,
              shadowColor: "#000",
              shadowOpacity: 0.2,
              shadowRadius: 8,
              shadowOffset: { width: 0, height: 4 },
              elevation: 8,
              transform: [{ translateY: snackAnim }],
            }}
          >
            <MaterialIcons name="check" size={18} color="#ffffff" />
            <Text style={{ fontSize: 15, fontWeight: "700", color: "#ffffff" }}>
              {filterSnackbar.msg}
            </Text>
          </Animated.View>
        )}
      </View>
    );
  }

  /* ── Web / iPad layout ───────────────────────── */
  return (
    <View style={[styles.screenRoot, isDark && { backgroundColor: "#141414" }, duplicatePanelOpen && { flexDirection: "row" } as any]}>
      <View
        style={[
          styles.container,
          duplicatePanelOpen && { flex: 1 } as any,
        ]}
      >
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.scrollContent}
        >
          <SustainQueueHeader
            subtitle={subtitle}
            viewMode={viewMode}
            onBackToPortal={() => onNavigate("Ticketing Portal")}
            onChangeView={setViewMode}
            teams={teams.map((t) => ({ id: t.id, name: t.name }))}
            viewingTeamId={viewingTeamId}
            onSelectTeam={setViewingTeamId}
            isViewingOwnTeam={isViewingOwnTeam}
          />

          {/* Read-Only Banner for Super Users, or when viewing another team's queue */}
          {effectiveReadOnly && (
            <View style={{
              backgroundColor: isDark ? "#2B1A00" : "#FFF4E5",
              borderLeftWidth: 4,
              borderLeftColor: "#FF8C09",
              paddingHorizontal: 16,
              paddingVertical: 12,
              marginBottom: 16,
              borderRadius: 6,
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            }}>
              <MaterialIcons name="visibility" size={20} color="#FF8C09" />
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: "600", color: isDark ? "#F2F2F2" : "#2d2d2d", marginBottom: 2 }}>
                  Read-Only Access
                </Text>
                <Text style={{ fontSize: 13, color: isDark ? "#B0B0B0" : "#5a5a5a" }}>
                  You have view-only access to this page. Contact your administrator to request edit permissions.
                </Text>
              </View>
            </View>
          )}

          <SustainQueueStatusSummary
            activeStatus={activeStatus}
            statusPills={statusPills}
            statusCounts={statusCounts}
            onStatusChange={setActiveStatus}
            isDark={isDark}
          />

          {/* Error state */}
          {queueError && (
            <View
              style={{
                backgroundColor: isDark ? "#2B1A00" : "#fff3cd",
                borderWidth: 1,
                borderColor: isDark ? "#5C3A00" : "#ffc107",
                borderRadius: 8,
                padding: 16,
                marginBottom: 16,
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <MaterialIcons name="error" size={24} color="#FF6B6B" style={{ marginTop: 2 }} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: "600", color: isDark ? "#F2F2F2" : "#333", marginBottom: 4 }}>
                  Unable to load tickets
                </Text>
                <Text style={{ fontSize: 13, color: isDark ? "#B0B0B0" : "#555", lineHeight: 18 }}>
                  Please refresh the page or try again later.
                </Text>
              </View>
            </View>
          )}

          {viewMode === "kanban" ? (
            <>
              <SustainQueueFilterBar
                query={query}
                filters={kanbanFilters}
                assignableUsers={assignableUsers}
                filterOptions={filterOptions}
                managerOptions={managerOptions}
                resultCount={filteredTickets.length}
                onQueryChange={setQuery}
                onFiltersChange={setKanbanFilters}
                isDark={isDark}
              />
              {queueError ? null : sustainQueueLoading ? (
                <View style={sqSkeleton.kanbanWrap}>
                  {[0, 1, 2].map((col) => (
                    <View key={`k-col-${col}`} style={sqSkeleton.kanbanCol}>
                      <SkeletonBlock pulse={skeletonPulse} height={12} width={140} style={{ marginBottom: 12, ...(isDark && { backgroundColor: "#2B2B2B" }) }} />
                      {[0, 1, 2].map((card) => (
                        <View key={`k-card-${col}-${card}`} style={[sqSkeleton.kanbanCard, isDark && { backgroundColor: "#202020", borderColor: "#363636" }]}>
                          <SkeletonBlock pulse={skeletonPulse} height={10} width={120} style={{ marginBottom: 8, ...(isDark && { backgroundColor: "#2B2B2B" }) }} />
                          <SkeletonBlock pulse={skeletonPulse} height={12} width="92%" style={{ marginBottom: 6, ...(isDark && { backgroundColor: "#2B2B2B" }) }} />
                          <SkeletonBlock pulse={skeletonPulse} height={12} width="68%" style={{ marginBottom: 10, ...(isDark && { backgroundColor: "#2B2B2B" }) }} />
                          <SkeletonBlock pulse={skeletonPulse} height={10} width="58%" style={isDark ? { backgroundColor: "#2B2B2B" } : undefined} />
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              ) : (
                <>
                <SustainQueueKanban
                    groups={groupedForKanban}
                    assignableUsers={assignableUsers}
                    totalCount={totalCount}
                    query={query}
                    hasActiveFilters={Object.values(kanbanFilters).some(arr => arr.length > 0)}
                    isReadOnly={effectiveReadOnly}
                    onTicketPress={handleTicketPress}
                    onMoveTicket={handleMoveTicket}
                    onQuickEdit={handleQuickUpdate}
                    onDuplicateBadgePress={handleDuplicateBadgePress}
                    scrollToTicketId={scrollToTicketId}
                    isDark={isDark}
                    colors={colors}
                  />
                  <QueuePaginationControls
                    totalCount={totalCount}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageChange={goToPage}
                    isDark={isDark}
                  />
                </>
              )}
            </>
          ) : (
            queueError ? null : sustainQueueLoading ? (
              <View style={[sqSkeleton.tableWrap, isDark && { backgroundColor: "#202020", borderColor: "#363636" }]}>
                <View style={[sqSkeleton.tableHeader, isDark && { backgroundColor: "#1a1a1a", borderBottomColor: "#363636" }]}>
                  {[0, 1, 2, 3, 4, 5].map((cell) => (
                    <SkeletonBlock key={`th-${cell}`} pulse={skeletonPulse} height={10} width={120} style={isDark ? { backgroundColor: "#2B2B2B" } : undefined} />
                  ))}
                </View>
                {[0, 1, 2, 3, 4, 5].map((row) => (
                  <View key={`tr-${row}`} style={[sqSkeleton.tableRow, isDark && { borderBottomColor: "#2B2B2B" }]}>
                    {[0, 1, 2, 3, 4, 5].map((cell) => (
                      <SkeletonBlock key={`td-${row}-${cell}`} pulse={skeletonPulse} height={10} width={cell === 1 ? 210 : 120} style={isDark ? { backgroundColor: "#2B2B2B" } : undefined} />
                    ))}
                  </View>
                ))}
              </View>
            ) : (
              <SustainQueueTable
                query={query}
                totalCount={totalCount}
                filteredTickets={filteredTicketsForTable}
                assignableUsers={assignableUsers}
                filterOptions={filterOptions}
                managerOptions={managerOptions}
                tableFilters={tableFilters}
                onTableFiltersChange={setTableFilters}
                onQueryChange={setQuery}
                onRowPress={handleTicketPress}
                onQuickEdit={handleQuickUpdate}
                onDuplicateBadgePress={handleDuplicateBadgePress}
                isReadOnly={effectiveReadOnly}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={goToPage}
                scrollToTicketId={scrollToTicketId}
                isDark={isDark}
                colors={colors}
              />
            )
          )}

        </ScrollView>
      </View>

      {/* Duplicates Panel - rendered outside padded container so it fills edge-to-edge */}
      {duplicatePanelOpen && selectedDuplicateTicketId && Platform.OS === "web" && (
        <View style={[{ width: 380, flexShrink: 0 }, isDark && { borderLeftColor: "#363636" }]}>
          <DuplicatesPanel
            primaryTicket={sustainQueue.find((t) => t.id === selectedDuplicateTicketId)!}
            allTickets={sustainQueue}
            mergingTicketId={mergingTicketId}
            resolvedDuplicates={panelDuplicates}
            duplicatesLoading={panelDuplicatesLoading}
            onViewDetails={handleViewDuplicateDetails}
            onMerge={handleMergeFromPanel}
            onClose={() => { setDuplicatePanelOpen(false); setPanelDuplicates(undefined); }}
            isDark={isDark}
          />
        </View>
      )}

      {closeModalTicket && (
        <CloseTicketModal
          visible={showCloseTicketModal}
          onClose={() => {
            setShowCloseTicketModal(false);
            setPendingCloseUpdate(null);
          }}
          ticketId={closeModalTicket.id}
          ticketCategory={closeModalTicket.category}
          ticketSubCategory={closeModalTicket.subcategory}
          categoryConfig={categoryConfig}
          onSubmit={handleCloseFromKanbanQuickEdit}
        />
      )}
    </View>
  );
};

const sqSkeleton = StyleSheet.create({
  kanbanWrap: {
    flexDirection: "row",
    gap: 12,
    paddingTop: 12,
    paddingBottom: 8,
  },
  kanbanCol: {
    flex: 1,
    minWidth: 260,
  },
  kanbanCard: {
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
  },
  tableWrap: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#ffffff",
  },
  tableHeader: {
    flexDirection: "row",
    gap: 18,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
  },
  tableRow: {
    flexDirection: "row",
    gap: 18,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
});

const paginationStyles = StyleSheet.create({
  wrap: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 8,
    paddingVertical: 16,
  },
  pageControlsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  button: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: "#d0d0d0",
    borderRadius: 6,
    backgroundColor: "#ffffff",
  },
  buttonDisabled: {
    borderColor: "#e5e7eb",
    backgroundColor: "#f9fafb",
  },
  buttonText: {
    fontSize: 13,
    color: "#005CBC",
    fontWeight: "600",
  },
  buttonTextDisabled: {
    color: "#bbb",
  },
  metaText: {
    fontSize: 13,
    color: "#555",
  },
  pageNumberBtn: {
    minWidth: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#d0d0d0",
    borderRadius: 6,
    backgroundColor: "#ffffff",
  },
  pageNumberBtnActive: {
    borderColor: "#005CBC",
    backgroundColor: "#005CBC",
  },
  pageNumberText: {
    fontSize: 13,
    color: "#005CBC",
    fontWeight: "600",
  },
  pageNumberTextActive: {
    color: "#ffffff",
  },
  ellipsis: {
    fontSize: 13,
    color: "#777",
    paddingHorizontal: 2,
  },
});

export default SustainQueueScreen;
